import { useEffect, useId, useRef, useState } from 'react'
import {
  BuildingsIcon,
  CheckIcon,
  ChevronDownIcon,
  cn,
  HospitalIcon,
  ShieldCheckIcon,
} from '@quickmd-llc/design-system'
import type { TreatmentCenterApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import './LocationSelector.scss'

type TreatmentCenter = Pick<
  TreatmentCenterApiOutContract,
  'TreatmentCenterId' | 'Name' | 'StreetAddress1' | 'City' | 'StateCode' | 'IsTemporary'
>

type Props = {
  locations?: TreatmentCenter[] | null
  selectedLocation: TreatmentCenter | null
  onSelect: (loc: TreatmentCenter) => void
  showError: boolean
  loading?: boolean
}

function formatAddress(loc: TreatmentCenter): string {
  return [loc.StreetAddress1, loc.City, loc.StateCode].filter(Boolean).join(', ')
}

const LocationSelector = ({ locations: locationsProp, selectedLocation, onSelect, showError, loading }: Props) => {
  const triggerId = useId()
  const labelId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const locations = locationsProp || []

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (e.target instanceof Node && wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  if (loading) {
    return (
      <div className='loc-sel-wrapper'>
        <div className='loc-sel-loading' data-testid='location-selector-loading'>
          Loading locations…
        </div>
      </div>
    )
  }

  // In edit mode a saved (possibly deactivated) location may be the only option;
  // include it in the list so the dropdown still renders correctly.
  const displayLocations = locations.length === 0 && selectedLocation ? [selectedLocation] : locations

  if (displayLocations.length === 0) {
    return (
      <div className='loc-sel-wrapper'>
        <div className='loc-sel-empty' data-testid='location-empty-state'>
          No state licenses found matching existing treatment locations
        </div>
      </div>
    )
  }

  return (
    <div className={cn('loc-sel-wrapper', showError && 'loc-sel-wrapper--error')} ref={wrapperRef}>
      <span id={labelId} className='loc-sel-label'>
        Location*
      </span>

      <div className='loc-sel-control'>
        <button
          id={triggerId}
          type='button'
          className={cn('loc-sel-trigger', isOpen && 'loc-sel-trigger--open', showError && 'loc-sel-trigger--error')}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          aria-labelledby={`${labelId} ${triggerId}`}>
          <span className={cn('loc-sel-trigger-text', !selectedLocation && 'loc-sel-trigger-text--placeholder')}>
            {selectedLocation ? selectedLocation.Name : 'Select location'}
          </span>
          <ChevronDownIcon iconSize='s' className={cn('loc-sel-chevron', isOpen && 'loc-sel-chevron--open')} />
        </button>

        {isOpen ?
          <div className='loc-sel-panel' role='listbox'>
            <div className='loc-sel-panel-header'>
              <ShieldCheckIcon iconSize='s' className='loc-sel-panel-header-icon' />
              <span>ADMIN-MANAGED LOCATIONS</span>
            </div>

            <ul className='loc-sel-list'>
              {displayLocations.map((loc) => {
                const isSelected = selectedLocation?.TreatmentCenterId === loc.TreatmentCenterId
                return (
                  <li
                    key={loc.TreatmentCenterId}
                    role='option'
                    aria-selected={isSelected}
                    tabIndex={0}
                    className={cn('loc-sel-item', isSelected && 'loc-sel-item--selected')}
                    onClick={() => {
                      onSelect(loc)
                      setIsOpen(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelect(loc)
                        setIsOpen(false)
                      }
                    }}>
                    <span
                      className={cn(
                        'loc-sel-item-icon',
                        loc.IsTemporary ? 'loc-sel-item-icon--popup' : 'loc-sel-item-icon--clinic',
                      )}>
                      {loc.IsTemporary ?
                        <BuildingsIcon iconSize='m' className='loc-sel-item-icon--popup-color' />
                      : <HospitalIcon iconSize='m' className='loc-sel-item-icon--clinic-color' />}
                    </span>

                    <span className='loc-sel-item-body'>
                      <span className='loc-sel-item-name-row'>
                        <span className='loc-sel-item-name'>{loc.Name}</span>
                        {loc.IsTemporary ?
                          <span className='loc-sel-item-popup-badge'>POP UP</span>
                        : null}
                      </span>
                      <span className='loc-sel-item-address'>{formatAddress(loc)}</span>
                    </span>

                    {isSelected ?
                      <span className='loc-sel-item-check'>
                        <CheckIcon iconSize='s' className='loc-sel-item-check-icon' />
                      </span>
                    : null}
                  </li>
                )
              })}
            </ul>
          </div>
        : null}
      </div>

      {showError ?
        <span className='loc-sel-error'>Please select a location</span>
      : null}
    </div>
  )
}

export default LocationSelector
